/**
 * Which organisation this browser is acting as.
 *
 * Most people belong to one organisation and never see this. A Venox
 * operator can open any organisation from the platform screen; the choice is
 * kept for the tab (sessionStorage, so closing it forgets) and sent on every
 * request as `x-baros-org`, which the API and the chat both honour. Every
 * client builds its headers through `authHeaders` so none can forget it.
 */

import { elevatedToken, tabletKey } from "./tablet.js";

const KEY = "baros.actingOrg";

export function actingOrg(): string | null {
  try {
    return window.sessionStorage.getItem(KEY);
  } catch {
    return null;
  }
}

/** Set (or clear with null) the acting organisation. The caller reloads. */
export function setActingOrg(orgId: string | null): void {
  try {
    if (orgId) window.sessionStorage.setItem(KEY, orgId);
    else window.sessionStorage.removeItem(KEY);
  } catch {
    // Private mode with storage blocked: the operator simply stays home.
  }
}

/**
 * The bearer token, plus the acting organisation when one is set.
 *
 * On an enrolled tablet (#80) the "token" is the tablet's own
 * `Device <key> <unlock>` value and goes as it is, with no acting org — a
 * tablet belongs to one. While somebody has just signed in with their
 * password for a fenced action, that sign-in is used instead, in the
 * tablet's organisation.
 */
export function authHeaders(token: string): Record<string, string> {
  const full = elevatedToken();
  if (full) {
    const key = tabletKey();
    return { authorization: `Bearer ${full}`, ...(key ? { "x-baros-org": key.orgId } : {}) };
  }
  if (token.startsWith("Device ")) return { authorization: token };
  const acting = actingOrg();
  return {
    authorization: `Bearer ${token}`,
    ...(acting ? { "x-baros-org": acting } : {}),
  };
}
